// pages/TraitCategoriesPage.tsx
import React, { useState, useEffect } from "react";
import { TraitCategory } from "../../types";
import { useApp } from "../../context/AppContext";
import { 
  getTraitCategories, 
  createTraitCategory, 
  updateTraitCategory, 
  deleteTraitCategory 
} from "../../services/traitCategoryService";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import FormSection from "../../components/ui/FormSection";
import { Input } from "../../components/ui/FormFields";
import { toast } from "react-hot-toast";

const BLANK_CATEGORY = {
  name: ""
};

const TraitCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<TraitCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCategory, setEditCategory] = useState<TraitCategory | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<TraitCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState(BLANK_CATEGORY);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const fetchedCategories = await getTraitCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (k: keyof typeof BLANK_CATEGORY) => (v: any) => {
    setCategoryForm(f => ({ ...f, [k]: v }));
  };

  const openNewCategory = () => {
    setEditCategory(null);
    setCategoryForm({ ...BLANK_CATEGORY });
    setShowModal(true);
  };

  const openEditCategory = (c: TraitCategory) => {
    setEditCategory(c);
    setCategoryForm({
      name: c.name
    });
    setShowModal(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      if (editCategory) {
        await updateTraitCategory(editCategory.id, categoryForm);
        toast.success("Category updated successfully");
      } else {
        await createTraitCategory(categoryForm);
        toast.success("Category created successfully");
      }
      await loadData();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Failed to save category");
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCategory) return;
    try {
      await deleteTraitCategory(deleteCategory.id);
      toast.success("Category deleted successfully");
      await loadData();
      setDeleteCategory(null);
    } catch (error: any) {
      console.error("Error deleting category:", error);
      toast.error(error.message || "Failed to delete category");
    }
  };

  return (
    <PageShell 
      title="Trait Categories" 
      subtitle="Manage trait categories for personality assessment"
      action={{ label: "Add Category", onClick: openNewCategory }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((c) => (
          <GlassCard key={c.id} className="p-5 hover:shadow-lg transition-all duration-300 group">
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-extrabold text-gray-800">{c.name}</h3>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="ghost" onClick={() => openEditCategory(c)}>✏️</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteCategory(c)}>🗑</Button>
                </div>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {categories.length === 0 && !loading && (
        <GlassCard className="p-12 text-center">
          <div className="text-6xl mb-4">📁</div>
          <p className="text-gray-500">No categories yet. Click "Add Category" to create your first trait category.</p>
        </GlassCard>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} 
             title={editCategory ? "Edit Category" : "Create New Category"}>
        <div className="space-y-5">
          <FormSection title="Category Information" icon="📁">
            <Input 
              label="Category Name" 
              value={categoryForm.name} 
              onChange={handleFormChange("name")} 
              required 
              placeholder="e.g., Personality, Emotional Intelligence, Leadership" 
            />
          </FormSection>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveCategory} className="bg-gradient-to-r from-indigo-600 to-purple-600">
              {editCategory ? "Update Category" : "Create Category"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteCategory} onClose={() => setDeleteCategory(null)}
        onConfirm={confirmDeleteCategory}
        title="Delete Category" 
        message={`Delete category "${deleteCategory?.name}"? All traits in this category will become uncategorized.`}
      />
    </PageShell>
  );
};

export default TraitCategoriesPage;